import { Spacing } from '@persona-id/design-tokens';
import { useState } from 'react';
import styled from 'styled-components';

import SearchInput from 'dashboard/components-lib/SearchInput';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { User } from 'dashboard/models/user';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';

const RoleStickersContainer = styled('div')`
  padding-top: ${Spacing[3]};
  display: flex;
  grid-gap: ${Spacing[2]};
  flex-wrap: wrap;
`;

const AddUsersListWrapper = styled('div')`
  overflow: auto;
`;

const SearchWrapper = styled('div')`
  top: 0;
  position: sticky;
  background: ${({ theme }) => theme.color.background.primary};

  padding-left: ${Spacing[7]};
  padding-right: ${Spacing[7]};

  z-index: 1;
`;

const StyledButtonGroup = styled(HStack)`
  bottom: 0;
  position: sticky;
  background: ${({ theme }) => theme.color.background.primary};

  padding: ${Spacing[3]} ${Spacing[7]} ${Spacing[7]} ${Spacing[7]};
`;

const UserRowsWrapper = styled('div')`
  padding-top: ${Spacing[3]};
  padding-left: ${Spacing[7]};
  padding-right: ${Spacing[7]};

  overflow-y: auto;
`;

export interface AddUsersListProps {
  activeUsers: User[];
  userIdToRoleNames: Record<string, string[]>;
  addUsers: (userIds: string[]) => void;
  handleCancel: () => void;
}

const AddUsersList = ({
  activeUsers,
  userIdToRoleNames,
  addUsers,
  handleCancel,
}: AddUsersListProps) => {
  const clearedUserIdMap = activeUsers
    .map((user) => user.id)
    .reduce(
      (acc, id) => {
        return {
          ...acc,
          [id]: false,
        };
      },
      {} as Record<string, boolean>,
    );

  const [selectedUserIdMap, setSelectedUserIdMap] = useState(clearedUserIdMap);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = activeUsers.filter((user) =>
    user.attributes?.['email-address']?.includes(searchTerm),
  );

  const userRows = filteredUsers.map((user) => {
    const checked = selectedUserIdMap[user.id];
    const roleNames = userIdToRoleNames[user.id];

    return (
      <TableTR
        key={user.id}
        onClick={() => {
          setSelectedUserIdMap({ ...selectedUserIdMap, [user.id]: !checked });
        }}
      >
        <TableTD>
          <Checkbox checked={checked} />
        </TableTD>
        <TableTD>{user.attributes['email-address']}</TableTD>
        <TableTD>
          <RoleStickersContainer>
            {roleNames.map((roleName) => {
              return <Sticker key={`${user.id}-${roleName}`}>{roleName}</Sticker>;
            })}
          </RoleStickersContainer>
        </TableTD>
      </TableTR>
    );
  });

  return (
    <AddUsersListWrapper>
      <SearchWrapper>
        <SearchInput
          id='add-users-list__search-input'
          handleInput={setSearchTerm}
          value={searchTerm}
          placeholder='Search by user'
          size='md'
        />
      </SearchWrapper>
      <Spacer height={1} />
      <UserRowsWrapper>
        <TableLayout
          showHeader={activeUsers.length > 0}
          isEmpty={activeUsers.length === 0}
          headerTR={
            <>
              <TableTH>{/* Checkbox */}</TableTH>
              <TableTH>Emails</TableTH>
              <TableTH>Role</TableTH>
            </>
          }
          nullState={<></>}
        >
          {userRows}
        </TableLayout>
      </UserRowsWrapper>
      <StyledButtonGroup equal>
        <Button id='form-add-users--cancel' variant='basic' onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          id='form-add-users--update'
          variant='primary'
          onClick={() =>
            addUsers(
              Object.entries(selectedUserIdMap)
                .filter(([_, checked]) => checked)
                .map(([userId, _]) => userId),
            )
          }
          disabled={!Object.values(selectedUserIdMap).some((isChecked) => isChecked)}
        >
          Update
        </Button>
      </StyledButtonGroup>
    </AddUsersListWrapper>
  );
};

export default AddUsersList;
